"use client";

import { c as _c } from "react/compiler-runtime";
import { ChatClient } from "@ably/chat";
import { B3_AUTH_COOKIE_NAME } from "@b3dotfun/sdk/shared/constants";
import * as Ably from "ably";
import Cookies from "js-cookie";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";
interface AblyChatProviderProps {
  children: ReactNode;
}

// Context to track whether Ably is available (for components to check before using hooks)
const AblyChatContext = createContext<{
  isAvailable: boolean;
}>({
  isAvailable: false
});

// Tracks whether the Ably ChatClient is ready. Components that need Ably SDK hooks
// should check useAblyChatAvailable() and only render Ably-dependent content when true.
const ChatClientPresentContext = createContext(false);

/**
 * Hook to check if Ably chat is available AND ChatClientProvider is in the ancestor tree.
 * Components using Ably hooks (useChatClient, usePresence, etc.) should check this
 * before rendering Ably-dependent content.
 */
export function useAblyChatAvailable() {
  return useContext(ChatClientPresentContext);
}

// Custom auth headers function to include JWT token
async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const userJwt = Cookies.get(B3_AUTH_COOKIE_NAME);
    return userJwt ? {
      Authorization: `Bearer ${userJwt}`
    } : {};
  } catch (error) {
    console.error("Failed to get JWT token from cookies:", error);
    return {};
  }
}

/**
 * Inner provider that handles Ably initialization.
 * SSR-safe: all browser-only work (Cookies, Ably connection) runs inside useEffect.
 */
function AblyChatProviderInner(t0) {
  const $ = _c(19);
  const {
    children
  } = t0;
  const [chatClient, setChatClient] = useState(null);
  const [realtimeClient, setRealtimeClient] = useState(null);
  const [userJwt, setUserJwt] = useState();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const read = () => {
        const jwt = Cookies.get(B3_AUTH_COOKIE_NAME);
        setUserJwt(prev => prev === jwt ? prev : jwt);
      };
      read();
      const id = window.setInterval(read, 5000);
      return () => window.clearInterval(id);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const clientIdentity = userJwt ?? null;
  let t3;
  let t4;
  if ($[2] !== clientIdentity) {
    t3 = () => {
      if (!clientIdentity) {
        return;
      }
      let ablyClientRef;
      ablyClientRef = null;
      let isCancelled;
      isCancelled = false;
      const initializeAbly = async function initializeAbly() {
        ;
        try {
          const realtime = new Ably.Realtime({
            authUrl: "/api/ably-auth",
            authMethod: "POST",
            authHeaders: await getAuthHeaders(),
            autoConnect: true,
            disconnectedRetryTimeout: 15000,
            suspendedRetryTimeout: 30000,
            httpRequestTimeout: 15000,
            closeOnUnload: false
          });
          ablyClientRef = realtime;
          await new Promise((resolve, reject) => {
            const onConnected = () => {
              realtime.connection.off("failed", onFailed);
              realtime.connection.off("suspended", onFailed);
              realtime.connection.off("closed", onFailed);
              console.log("Ably connected with clientId:", realtime.auth.clientId);
              resolve();
            };
            const onFailed = stateChange => {
              realtime.connection.off("connected", onConnected);
              realtime.connection.off("failed", onFailed);
              realtime.connection.off("suspended", onFailed);
              realtime.connection.off("closed", onFailed);
              if (!isCancelled) {
                console.error("Ably connection failed:", stateChange.reason);
              }
              reject(stateChange.reason);
            };
            if (realtime.connection.state === "connected") {
              resolve();
            } else {
              realtime.connection.once("connected", onConnected);
              realtime.connection.once("failed", onFailed);
              realtime.connection.once("suspended", onFailed);
              realtime.connection.once("closed", onFailed);
            }
          });
          if (!isCancelled) {
            const client = new ChatClient(realtime);
            setChatClient(client);
            setRealtimeClient(realtime);
          } else {
            realtime.close();
          }
        } catch (t5) {
          const error = t5;
          if (!isCancelled) {
            console.error("Failed to initialize Ably:", error);
          }
        }
      };
      initializeAbly();
      return () => {
        isCancelled = true;
        setRealtimeClient(null);
        if (ablyClientRef) {
          ablyClientRef.close();
        }
      };
    };
    t4 = [clientIdentity];
    $[2] = clientIdentity;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  const t5 = !!chatClient;
  let t6;
  if ($[5] !== t5) {
    t6 = {
      isAvailable: t5
    };
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const contextValue = t6;
  const t7 = !!chatClient;
  let t8;
  if ($[7] !== children || $[8] !== t7) {
    t8 = <ChatClientPresentContext.Provider value={t7}>{children}</ChatClientPresentContext.Provider>;
    $[7] = children;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== chatClient || $[11] !== t8) {
    t9 = <AblyChatClientContext.Provider value={chatClient}>{t8}</AblyChatClientContext.Provider>;
    $[10] = chatClient;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== realtimeClient || $[14] !== t9) {
    t10 = <AblyRealtimeContext.Provider value={realtimeClient}>{t9}</AblyRealtimeContext.Provider>;
    $[13] = realtimeClient;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== contextValue || $[17] !== t10) {
    t11 = <AblyChatContext.Provider value={contextValue}>{t10}</AblyChatContext.Provider>;
    $[16] = contextValue;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  return t11;
}

/**
 * Ably Chat Provider.
 * Always renders AblyChatProviderInner to keep the React tree structure stable
 * across SSR and hydration. The inner component uses useEffect for initialization
 * (which doesn't run during SSR), so the tree is SSR-safe without a conditional
 * isMounted guard that would change the root element type and cause React to
 * unmount/remount all children on hydration.
 */
export default function AblyChatProvider(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <AblyChatProviderInner>{children}</AblyChatProviderInner>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// Context that holds the ChatClient directly. Consumers read from this instead of
// the Ably SDK's ChatClientProvider, so the provider tree stays stable (no conditional
// wrapping that causes full-page remounts).
const AblyChatClientContext = createContext<ChatClient | null>(null);

/**
 * Hook to get the Ably ChatClient instance (or null if not yet connected).
 * Consumers that need Ably SDK hooks (useMessages, usePresence, etc.) should
 * wrap their subtree with ChatClientProvider using this client at point of use.
 */
export function useAblyChatClient() {
  return useContext(AblyChatClientContext);
}

// Underlying Ably.Realtime client. Exposed so feature hooks like
// usePartnerRealtime can subscribe to plain pub/sub channels without
// constructing their own Ably connection per mount (which previously stacked
// up TCP sessions on every route change with a stale partnerId).
const AblyRealtimeContext = createContext<Ably.Realtime | null>(null);

/**
 * Hook to get the shared Ably.Realtime instance (or null if not yet
 * connected). Use this for plain pub/sub features; do not call .close() on
 * the returned client — the provider owns its lifecycle.
 */
export function useAblyRealtime() {
  return useContext(AblyRealtimeContext);
}